import { useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { AssetFormDialog } from "./AssetFormDialog";
import { ContractFormDialog } from "./ContractFormDialog";
import {
  useAssets,
  useContracts,
  useDeleteAsset,
  useDeleteContract,
} from "./hooks";
import {
  ASSET_STATUS_LABELS,
  ASSET_TYPE_LABELS,
  CONTRACT_TYPE_LABELS,
  expiryBadge,
} from "./labels";
import { getAsset, type AssetDetail, type ContractListItem } from "./api";

const PAGE_SIZE = 10;
type Tab = "assets" | "contracts";

export default function AssetsPage() {
  const [tab, setTab] = useState<Tab>("assets");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Assets</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Customer hardware and software, with AMC / warranty coverage.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist" aria-label="Asset views">
        {(["assets", "contracts"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={
              "border-b-2 px-4 py-2 text-sm font-medium " +
              (tab === t
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {t === "assets" ? "Assets" : "AMC contracts"}
          </button>
        ))}
      </div>

      {tab === "assets" ? <AssetsTab /> : <ContractsTab />}
    </div>
  );
}

function AssetsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);

  const filters = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim(),
    asset_type: typeFilter,
    status: statusFilter,
    ...(expiringOnly ? { warranty_expiring_days: 30 } : {}),
  };

  const { data, isLoading, isError, error } = useAssets(filters);
  const remove = useDeleteAsset();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AssetDetail | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  async function openEdit(id: string) {
    setActionError(null);
    try {
      setEditing(await getAsset(id));
      setFormOpen(true);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Could not load the asset.");
    }
  }

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              aria-label="Search assets"
              placeholder="Tag, name, serial, model"
              className="pl-8"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            aria-label="Filter by type"
            className="w-auto min-w-[130px]"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All types</option>
            {Object.entries(ASSET_TYPE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by status"
            className="w-auto min-w-[130px]"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {Object.entries(ASSET_STATUS_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={expiringOnly}
              onChange={(e) => {
                setExpiringOnly(e.target.checked);
                setPage(1);
              }}
            />
            Warranty expiring
          </label>
        </div>
        <RequirePermission permission="asset:manage">
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add asset
          </Button>
        </RequirePermission>
      </div>

      {actionError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Asset</th>
              <th className="px-4 py-2.5 font-medium">Customer</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Warranty</th>
              <th className="px-4 py-2.5 font-medium">Contract</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load assets."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No assets match these filters.
                </td>
              </tr>
            )}
            {data?.items.map((a) => {
              const warranty = expiryBadge(a.warranty_status, a.days_to_warranty_expiry);
              return (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {a.asset_tag}
                      {a.serial_number ? ` · ${a.serial_number}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{a.customer_name ?? "—"}</td>
                  <td className="px-4 py-3">{ASSET_TYPE_LABELS[a.asset_type]}</td>
                  <td className="px-4 py-3">{ASSET_STATUS_LABELS[a.status]}</td>
                  <td className="px-4 py-3">
                    <Badge tone={warranty.tone}>{warranty.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {a.amc_contract_number ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <RequirePermission permission="asset:manage">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" aria-label={`Edit ${a.name}`} onClick={() => void openEdit(a.id)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <ConfirmButton
                          aria-label={`Remove ${a.name}`}
                          title="Delete asset?"
                          message={<>Remove asset <span className="font-medium text-foreground">{a.name}</span>?</>}
                          onConfirm={() => run(() => remove.mutateAsync(a.id))}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </ConfirmButton>
                      </div>
                    </RequirePermission>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pager
        total={data?.total ?? 0}
        noun="asset"
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <AssetFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}

function ContractsTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);

  const filters = {
    page,
    page_size: PAGE_SIZE,
    search: search.trim(),
    contract_type: typeFilter,
    ...(expiringOnly ? { expiring_days: 30 } : {}),
  };

  const { data, isLoading, isError, error } = useContracts(filters);
  const remove = useDeleteContract();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContractListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  async function run(fn: () => Promise<unknown>) {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <Input
              aria-label="Search contracts"
              placeholder="Number or provider"
              className="pl-8"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <Select
            aria-label="Filter by contract type"
            className="w-auto min-w-[150px]"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All types</option>
            {Object.entries(CONTRACT_TYPE_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </Select>
          <label className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={expiringOnly}
              onChange={(e) => {
                setExpiringOnly(e.target.checked);
                setPage(1);
              }}
            />
            Expiring soon
          </label>
        </div>
        <RequirePermission permission="asset:manage">
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add contract
          </Button>
        </RequirePermission>
      </div>

      {actionError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {actionError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Contract</th>
              <th className="px-4 py-2.5 font-medium">Customer</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Period</th>
              <th className="px-4 py-2.5 font-medium">Coverage</th>
              <th className="px-4 py-2.5 font-medium">Assets</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load contracts."}
                </td>
              </tr>
            )}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  No contracts match these filters.
                </td>
              </tr>
            )}
            {data?.items.map((c) => {
              const coverage = expiryBadge(c.coverage_status, c.days_to_expiry);
              return (
                <tr key={c.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.contract_number}</div>
                    <div className="text-xs text-muted-foreground">{c.provider ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.customer_name ?? "—"}</td>
                  <td className="px-4 py-3">{CONTRACT_TYPE_LABELS[c.contract_type]}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {c.start_date} → {c.end_date}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={coverage.tone}>{coverage.label}</Badge>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{c.asset_count}</td>
                  <td className="px-4 py-3">
                    <RequirePermission permission="asset:manage">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          aria-label={`Edit ${c.contract_number}`}
                          onClick={() => {
                            setEditing(c);
                            setFormOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <ConfirmButton
                          aria-label={`Remove ${c.contract_number}`}
                          title="Delete contract?"
                          message={<>Remove contract <span className="font-medium text-foreground">{c.contract_number}</span>?</>}
                          onConfirm={() => run(() => remove.mutateAsync(c.id))}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </ConfirmButton>
                      </div>
                    </RequirePermission>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pager
        total={data?.total ?? 0}
        noun="contract"
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <ContractFormDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}

function Pager({
  total,
  noun,
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  total: number;
  noun: string;
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span>{`${total} ${noun}${total === 1 ? "" : "s"}`}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" disabled={page <= 1} onClick={onPrev}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button variant="outline" disabled={page >= totalPages} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
